import {
  Accordion,
  Badge,
  Card,
  Col,
  Container,
  ListGroup,
  Row,
  Table,
} from "react-bootstrap";
import { useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import Fetch from "../../../../utils/fetch";
import { IFetchError } from "../../../../types";
import { Toast } from "../../../../utils";
import { TableLoader } from "../../../../components/Loaders";

interface IReservationRoomDetail {
  id: number;
  room_name: string;
  quantity: number;
  total_adults: number;
  total_childs: number;
  total_extra_beds: number;
  childs: number[]; // ages of children
  extra_beds: number[]; // ages of extra beds
  guests: {
    first_name: string;
    last_name: string;
    is_child: boolean;
  }[];
}

interface IReservationDetails {
  property_id: number;
  property_name: string;
  guest_name: string;
  guest_special_request: string | null;
  check_in: string;
  check_out: string;
  number_of_nights: number;
  total_rooms: number;
  total_adults: number;
  total_childs: number;
  status: "confirmed" | "canceled";
  ota_tracking_id: string | null;
  property_tracking_id: string | null;
  created_at: string;
  cancelation_at?: string;
  booking_source: string;
  b2b_created_user: string;
  extranet_cancelation_user?: string;
  cancelation_note?: string;
  receive_currency: string;
  receive_amount: number;
  exchange_rate: number;
  taxes?: {
    type: string;
    currency: string;
    is_included: boolean;
    amount: number;
  }[];
  meals: { id: string; name: string }[];
  cancellation_policies: { from: string; amount: number }[];
  room_details: IReservationRoomDetail[];
}

export default function ViewReservationDetails() {
  const navigate = useNavigate();
  const params = useParams();
  const reservationId = Number(params.reservation_id);

  const reservation_details = useQuery(
    ["reservation_details_v2", reservationId],
    async () =>
      await Fetch<IReservationDetails>({
        method: "GET",
        url: `${process.env.REACT_APP_BACKEND_URL}/api/b2b/v2/booking/property/reservation/${reservationId}`,
        navigate,
      })
  );

  if (reservation_details.isError) {
    Toast.fire({
      icon: "error",
      title: (reservation_details.error as IFetchError).message,
      position: "bottom-left",
      showCloseButton: true,
    });
  }

  return (
    <Container className="py-2">
      {reservation_details.isLoading ? (
        <>
          {[1, 2].map(() => (
            <Card className="mb-3 rounded-2">
              <Table>
                <TableLoader columnLength={4} />
              </Table>
            </Card>
          ))}
        </>
      ) : reservation_details.isSuccess ? (
        <>
          <Card className="pt-4 pb-2 px-3 rounded-2 my-3">
            <Table
              striped
              bordered
              hover
              size="sm"
              responsive
              id="reservations"
            >
              <thead>
                <tr>
                  <th className="text-center" colSpan={4}>
                    Reservation Details
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th>Reservation ID</th>
                  <td>{reservationId}</td>
                  <th>Property ID</th>
                  <td>{reservation_details.data.data.property_id}</td>
                </tr>

                <tr>
                  <th>Property Name</th>
                  <td>{reservation_details.data.data.property_name}</td>
                  <th>Guest Name</th>
                  <td>{reservation_details.data.data.guest_name}</td>
                </tr>

                {reservation_details.data.data.guest_special_request && (
                  <tr>
                    <th>Special Request</th>
                    <td colSpan={3}>
                      {reservation_details.data.data.guest_special_request}
                    </td>
                  </tr>
                )}

                <tr>
                  <th>Status</th>
                  <td>{reservation_details.data.data.status}</td>
                  <th>Price</th>
                  <td>
                    {reservation_details.data.data.receive_amount}{" "}
                    {reservation_details.data.data.receive_currency}
                  </td>
                </tr>

                <tr>
                  <th>Check In</th>
                  <td>{reservation_details.data.data.check_in}</td>
                  <th>Check Out</th>
                  <td>{reservation_details.data.data.check_out}</td>
                </tr>

                <tr>
                  <th>Total Nights</th>
                  <td>{reservation_details.data.data.number_of_nights}</td>
                  <th>Total Rooms</th>
                  <td>{reservation_details.data.data.total_rooms}</td>
                </tr>

                <tr>
                  <th>Total Adults</th>
                  <td>{reservation_details.data.data.total_adults}</td>
                  <th>Total Children</th>
                  <td>{reservation_details.data.data.total_childs}</td>
                </tr>

                {(reservation_details.data.data.ota_tracking_id ||
                  reservation_details.data.data.property_tracking_id) && (
                  <tr>
                    <th>B2B Confirmation ID</th>
                    <td>
                      {reservation_details.data.data.ota_tracking_id || "-"}
                    </td>
                    <th>Hotel Confirmation ID</th>
                    <td>
                      {reservation_details.data.data.property_tracking_id ||
                        "-"}
                    </td>
                  </tr>
                )}

                <tr>
                  <th>Created By</th>
                  <td>{reservation_details.data.data.b2b_created_user}</td>
                  <th>Creation Time</th>
                  <td>{reservation_details.data.data.created_at}</td>
                </tr>

                {reservation_details.data.data.extranet_cancelation_user && (
                  <tr>
                    <th>Canceled By</th>
                    <td>
                      {reservation_details.data.data.extranet_cancelation_user}
                    </td>
                    <th>Cancelation Time</th>
                    <td>
                      {reservation_details.data.data.cancelation_at
                        ? reservation_details.data.data.cancelation_at
                        : "-"}
                    </td>
                  </tr>
                )}

                {reservation_details.data.data.cancelation_note && (
                  <tr>
                    <th>Cancelation Note</th>
                    <td colSpan={3}>
                      {reservation_details.data.data.cancelation_note}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card>

          <Card className="py-2 px-3 rounded-2 my-3">
            <h4 className="my-3">Room Details</h4>
            <Accordion defaultActiveKey="0" className="custom-accordion">
              {reservation_details.data.data.room_details.map((room, idx) => (
                <Accordion.Item eventKey={idx.toString()} key={idx}>
                  <Accordion.Header>
                    <strong>{room.room_name}</strong>{" "}
                    <Badge bg="secondary" className="ms-2">
                      x{room.quantity}
                    </Badge>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Row className="mb-3">
                      <Col md={4}>
                        <Card.Subtitle className="mb-2">
                          Occupancy
                        </Card.Subtitle>
                        <div>
                          <strong>Adults:</strong> {room.total_adults}
                        </div>
                        {room.total_childs > 0 && (
                          <div>
                            <strong>Children:</strong> {room.total_childs}{" "}
                            {room.childs.map((age, i) => (
                              <Badge
                                bg="info"
                                text="dark"
                                className="me-1"
                                key={i}
                              >
                                Age {age}
                              </Badge>
                            ))}
                          </div>
                        )}
                        {room.total_extra_beds > 0 && (
                          <div className="mt-1">
                            <strong>Extra Beds:</strong> {room.total_extra_beds}{" "}
                            {room.extra_beds.map((age, i) => (
                              <Badge
                                bg="warning"
                                text="dark"
                                className="me-1"
                                key={i}
                              >
                                Age {age}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </Col>
                      <Col md={4}>
                        <Card.Subtitle className="mb-2 ">Meals</Card.Subtitle>
                        {reservation_details.data.data.meals.length > 0 ? (
                          reservation_details.data.data.meals.map((meal) => (
                            <Badge bg="success" className="me-1" key={meal.id}>
                              {meal.name}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-muted">No meals</span>
                        )}
                      </Col>
                      <Col md={4}>
                        <Card.Subtitle className="mb-2 ">Guests</Card.Subtitle>
                        <ListGroup variant="flush">
                          {room.guests.map((guest, i) => (
                            <ListGroup.Item key={i} className="ps-0">
                              {guest.first_name} {guest.last_name}{" "}
                              {guest.is_child && (
                                <Badge
                                  bg="warning"
                                  text="dark"
                                  className="ms-1"
                                >
                                  Child
                                </Badge>
                              )}
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          </Card>

          <Card className="pt-4 pb-2 px-3 rounded-2 my-3">
            <Table
              striped
              bordered
              hover
              size="sm"
              responsive
              id="reservations"
            >
              <thead>
                <tr>
                  <th className="text-center" colSpan={4}>
                    Cancellation Policy
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    {buildCancellationPolicyText(
                      reservation_details.data.data.cancellation_policies,
                      reservation_details.data.data.receive_currency
                    )}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Card>
        </>
      ) : (
        <h4 className="teaxt-center">Data is not Found!</h4>
      )}
    </Container>
  );
}

export function buildCancellationPolicyText(
  policies: { from: string; amount: number }[],
  currency: string
): string {
  if (!policies || policies.length === 0) {
    return "This reservation is non-refundable.";
  }

  // Build human-readable sentences
  const lines = policies.map((p) => {
    return `If cancelled on or after ${p.from}, a fee of ${p.amount.toFixed(
      2
    )} ${currency} applies.`;
  });

  return lines.join(" ");
}
